import React from 'react';
import { sb } from '../../../modules/state';
import { useAuth } from '../../../context/AuthContext';

interface BloodResult {
  insulin: number | null;
  potassium: number | null;
  sodium: number | null;
  albumin: number | null;
  egfr: number | null;
  magnesium: number | null;
  uric_acid: number | null;
  creatinine: number | null;
  urea: number | null;
  anion_gap: number | null;
  co2: number | null;
  chloride: number | null;
  test_date: string;
}

export function BloodEntry() {
  const { currentUser } = useAuth();
  const today = new Date().toISOString().slice(0, 10);
  const [markers, setMarkers] = React.useState<Record<string, string>>({
    date: today
  });
  const [isSaving, setIsSaving] = React.useState(false);

  const handleChange = (id: string, value: string) => {
    setMarkers(prev => ({ ...prev, [id]: value }));
  };

  const handleSave = async () => {
    if (!currentUser?.id) {
      console.error('User not authenticated');
      return;
    }

    setIsSaving(true);
    try {
      const now = new Date().toISOString();

      await sb.from('blood_results').insert({
        user_id: currentUser.id,
        test_date: markers['date'] || new Date().toISOString().slice(0, 10),
        insulin: markers['insulin'] || null,
        potassium: markers['potassium'] || null,
        sodium: markers['sodium'] || null,
        albumin: markers['albumin'] || null,
        egfr: markers['egfr'] || null,
        magnesium: markers['magnesium'] || null,
        uric_acid: markers['uric_acid'] || null,
        creatinine: markers['creatinine'] || null,
        urea: markers['urea'] || null,
        anion_gap: markers['anion_gap'] || null,
        total_co2: markers['co2'] || null,
        chloride: markers['chloride'] || null,
        logged_at: now
      });

      console.log('Blood panel saved successfully');
      setMarkers({});
    } catch (error) {
      console.error('Error saving blood panel:', error);
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="space-y-6 p-4">
      <section className="flex flex-col gap-2">
        <div className="flex justify-between items-end">
          <div>
            <h2 className="text-2xl font-bold tracking-tight text-on-surface">New Entry</h2>
            <p className="text-sm text-on-surface-variant">Log full panel metrics</p>
          </div>
          <div className="bg-surface-container-low px-4 py-2 rounded-xl flex items-center gap-3">
            <span className="material-symbols-outlined text-sm text-primary">calendar_today</span>
            <input
              className="bg-transparent border-none text-sm font-medium text-on-surface focus:ring-0 p-0 cursor-pointer placeholder:text-on-surface-variant"
              type="date"
              value={markers['date'] || ''}
              onChange={(e) => handleChange('date', e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 px-1">
          <div className="w-1.5 h-4 bg-primary rounded-full"></div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-primary">GOLD • TIER 1</h3>
          <div className="flex-grow h-px bg-primary/10 ml-2"></div>
        </div>
        <div className="bg-surface-container-low p-4 rounded-xl relative overflow-hidden">
          <div className="absolute top-0 right-0 w-32 h-32 bg-primary/5 rounded-full -mr-16 -mt-16 blur-3xl"></div>
          <div className="grid grid-cols-1 gap-4 relative z-10">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-on-surface-variant uppercase tracking-tighter">Insulin (Fasting) · 2.1–10.4 mIU/L</label>
              <input
                className="w-full bg-surface-container-highest border-none rounded-lg p-3 text-lg font-bold text-primary focus:ring-2 focus:ring-primary/20 transition-all placeholder:text-on-surface-variant"
                placeholder="-"
                type="number"
                value={markers['insulin'] || ''}
                onChange={(e) => handleChange('insulin', e.target.value)}
              />
            </div>
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 px-1">
          <div className="w-1.5 h-4 bg-error rounded-full"></div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-error">RED • CRITICAL</h3>
          <div className="flex-grow h-px bg-error/10 ml-2"></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-error/30">
            <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-tighter">Potassium · 3.5–5.1 mmol/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-error/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['potassium'] || ''}
              onChange={(e) => handleChange('potassium', e.target.value)}
            />
          </div>
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-error/30">
            <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-tighter">Sodium · 136–145 mmol/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-error/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['sodium'] || ''}
              onChange={(e) => handleChange('sodium', e.target.value)}
            />
          </div>
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-error/30">
            <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-tighter">Albumin · 35–52 g/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-error/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['albumin'] || ''}
              onChange={(e) => handleChange('albumin', e.target.value)}
            />
          </div>
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-error/30">
            <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-tighter">eGFR · {'>'}90 mL/min/1.73m²</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-error/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['egfr'] || ''}
              onChange={(e) => handleChange('egfr', e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 px-1">
          <div className="w-1.5 h-4 bg-secondary rounded-full"></div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-secondary">YELLOW • EARLY WARNING</h3>
          <div className="flex-grow h-px bg-secondary/10 ml-2"></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-secondary/30">
            <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-tighter text-secondary">Urea · &lt;8.4 mmol/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-secondary/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['urea'] || ''}
              onChange={(e) => handleChange('urea', e.target.value)}
            />
          </div>
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-secondary/30">
            <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-tighter text-secondary">Creatinine · 64–104 µmol/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-secondary/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['creatinine'] || ''}
              onChange={(e) => handleChange('creatinine', e.target.value)}
            />
          </div>
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-secondary/30">
            <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-tighter text-secondary">Uric Acid · 0.21–0.43 mmol/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-secondary/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['uric_acid'] || ''}
              onChange={(e) => handleChange('uric_acid', e.target.value)}
            />
          </div>
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-secondary/30">
            <label className="text-[10px] font-bold text-on-surface-variant uppercase tracking-tighter text-secondary">Magnesium · 0.7–1.1 mmol/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-secondary/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['magnesium'] || ''}
              onChange={(e) => handleChange('magnesium', e.target.value)}
            />
          </div>
        </div>
      </section>

      <section className="space-y-3">
        <div className="flex items-center gap-2 px-1">
          <div className="w-1.5 h-4 bg-success rounded-full"></div>
          <h3 className="text-xs font-bold uppercase tracking-widest text-success">GREEN • EXPECTED SKEW</h3>
          <div className="flex-grow h-px bg-success/10 ml-2"></div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-success/30">
            <label className="text-[10px] font-bold uppercase tracking-tighter text-success">Anion Gap · 10–20 mmol/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-success/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['anion_gap'] || ''}
              onChange={(e) => handleChange('anion_gap', e.target.value)}
            />
          </div>
          <div className="bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-success/30">
            <label className="text-[10px] font-bold uppercase tracking-tighter text-success">CO2 · 21–31 mmol/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-success/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['co2'] || ''}
              onChange={(e) => handleChange('co2', e.target.value)}
            />
          </div>
          <div className="col-span-2 bg-surface-container-low p-3 rounded-xl space-y-1 border-l-2 border-success/30">
            <label className="text-[10px] font-bold uppercase tracking-tighter text-success">Chloride · 98–107 mmol/L</label>
            <input
              className="w-full bg-surface-container-highest border-none rounded-lg p-2 text-md font-bold text-primary focus:ring-1 focus:ring-success/40 placeholder:text-on-surface-variant"
              placeholder="-"
              type="number"
              value={markers['chloride'] || ''}
              onChange={(e) => handleChange('chloride', e.target.value)}
            />
          </div>
        </div>
      </section>

      <div className="py-4 flex justify-center gap-2">
        <div className="w-2 h-2 rounded-full bg-primary shadow-[0_0_8px_rgba(255,233,176,0.5)]"></div>
        <div className="w-2 h-2 rounded-full bg-surface-container-highest"></div>
        <div className="w-2 h-2 rounded-full bg-surface-container-highest"></div>
        <div className="w-2 h-2 rounded-full bg-surface-container-highest"></div>
      </div>

      <button
        onClick={handleSave}
        disabled={isSaving}
        className="w-full bg-gradient-to-br from-primary to-secondary py-4 rounded-xl flex items-center justify-center gap-3 active:scale-95 transition-all shadow-xl shadow-primary/5 disabled:opacity-50 disabled:cursor-not-allowed"
      >
        {isSaving ? (
          <>
            <span className="text-on-primary font-bold tracking-tight uppercase">Saving...</span>
            <span className="material-symbols-outlined text-on-primary animate-spin">autorenew</span>
          </>
        ) : (
          <>
            <span className="text-on-primary font-bold tracking-tight uppercase">Save Results</span>
            <span className="material-symbols-outlined text-on-primary">bolt</span>
          </>
        )}
      </button>
    </div>
  );
}
